<script lang="ts">
  import { scheduleUndo } from '$lib/state/actionDialog.svelte';
  // Admin > Tracked Domains: maps a company's own email domain to a
  // vertical (and optionally a specific company) for a future mail-sync
  // engine to auto-file inbound/outbound mail. See internal/api/handlers/
  // admin_tracked_domains.go for the free/public-provider blocklist and
  // normalization this form's errors surface.
  import { createQuery, createMutation, useQueryClient } from '@tanstack/svelte-query';
  import { Plus, Trash2, Globe } from 'lucide-svelte';
  import { adminTrackedDomainsApi, adminVerticalsApi } from '$lib/api/admin';
  import { companiesApi } from '$lib/api/crm';
  import { getErrorMessage } from '$lib/api/client';
  import Modal from '$lib/components/ui/Modal.svelte';
  import type { TrackedDomain } from '$lib/types';

  const queryClient = useQueryClient();

  let showForm = $state(false);
  let formError = $state('');

  function emptyForm() {
    return { domain: '', vertical_id: undefined as number | undefined, company_id: undefined as number | undefined };
  }
  let form = $state(emptyForm());

  const domainsQuery = createQuery(() => ({
    queryKey: ['admin-tracked-domains'],
    queryFn: () => adminTrackedDomainsApi.list()
  }));
  const verticalsQuery = createQuery(() => ({
    queryKey: ['admin-verticals'],
    queryFn: () => adminVerticalsApi.list()
  }));
  // ponytail: a flat, unscoped lookup (per_page 100, no vertical filter) —
  // fine for the current company counts; if that ever gets tight, switch
  // this to companiesApi.list({ vertical_id: form.vertical_id }) scoped to
  // the form's selected vertical instead of one global fetch.
  const companiesQuery = createQuery(() => ({
    queryKey: ['admin-companies-lookup'],
    queryFn: () => companiesApi.list({ per_page: 100 })
  }));

  $effect(() => {
    if (form.vertical_id === undefined && verticalsQuery.data?.results.length) {
      form.vertical_id = verticalsQuery.data.results[0].id;
    }
  });

  // Grouped by vertical for the section-headered table below. The backend
  // already orders results by vertical name then domain (see
  // adminListTrackedDomains), so a single pass preserves that order.
  const groupedDomains = $derived.by(() => {
    const groups: { verticalId: number; verticalName: string; rows: TrackedDomain[] }[] = [];
    for (const row of domainsQuery.data?.results ?? []) {
      const last = groups[groups.length - 1];
      if (last && last.verticalId === row.vertical_id) last.rows.push(row);
      else groups.push({ verticalId: row.vertical_id, verticalName: row.vertical_name, rows: [row] });
    }
    return groups;
  });

  const saveMutation = createMutation(() => ({
    mutationFn: (body: Record<string, unknown>) => adminTrackedDomainsApi.create(body),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin-tracked-domains'] });
      closeForm();
    },
    onError: (err: unknown) => (formError = getErrorMessage(err))
  }));

  const deleteMutation = createMutation(() => ({
    mutationFn: (id: number) => adminTrackedDomainsApi.remove(id),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['admin-tracked-domains'] })
  }));

  function openCreate() {
    form = emptyForm();
    if (verticalsQuery.data?.results.length) form.vertical_id = verticalsQuery.data.results[0].id;
    formError = '';
    showForm = true;
  }

  function closeForm() {
    showForm = false;
  }

  function submit(e: SubmitEvent) {
    e.preventDefault();
    if (!form.domain.trim() || !form.vertical_id) {
      formError = 'Domain and vertical are required';
      return;
    }
    formError = '';
    saveMutation.mutate({
      domain: form.domain.trim(),
      vertical_id: form.vertical_id,
      company_id: form.company_id ?? null
    });
  }

  function remove(row: TrackedDomain) {
    scheduleUndo(`Stop tracking "${row.domain}"`, () => deleteMutation.mutate(row.id));
  }
</script>

<div>
  <div class="mb-[18px] flex items-center">
    <div>
      <h2 class="tym-section-title">Tracked domains</h2>
      <div class="tym-section-sub">Company email domains &middot; used to auto-file mail by vertical/company</div>
    </div>
    <span class="flex-1"></span>
    <button type="button" class="btn-primary" onclick={openCreate}>
      <Plus class="h-4 w-4" /> New domain
    </button>
  </div>

  {#if domainsQuery.isLoading}
    <p class="tym-loading-state">Loading tracked domains&hellip;</p>
  {:else if domainsQuery.isError}
    <p class="tym-error-state">{getErrorMessage(domainsQuery.error)}</p>
  {:else if domainsQuery.data}
    <div class="tym-table-wrap max-w-[820px]">
      <div class="tym-table-scroll">
        <table class="tym-table">
          <thead>
            <tr>
              <th>Domain</th>
              <th>Company</th>
              <th>Added by</th>
              <th>Added</th>
              <th>Captured</th>
              <th class="num"></th>
            </tr>
          </thead>
          {#each groupedDomains as group (group.verticalId)}
            <tbody>
              <tr>
                <td colspan="6" class="bg-toyama-light-carbon text-[11.5px] font-heading font-bold uppercase tracking-wide text-navy">
                  {group.verticalName}
                </td>
              </tr>
              {#each group.rows as row (row.id)}
                <tr>
                  <td class="font-semibold text-navy">
                    <span class="inline-flex items-center gap-1.5"><Globe class="h-3.5 w-3.5 text-dark-gray" />{row.domain}</span>
                  </td>
                  <td class="muted">{row.company_name ?? '—'}</td>
                  <td class="muted">{row.created_by_name ?? row.created_by}</td>
                  <td class="muted">{new Date(row.created_at).toLocaleDateString()}</td>
                  <td class="muted">{row.email_count} email{row.email_count === 1 ? '' : 's'} captured</td>
                  <td class="num">
                    <button
                      type="button"
                      class="btn-danger !h-10 !w-10 !p-0"
                      onclick={() => remove(row)}
                      aria-label={`Stop tracking ${row.domain}`}
                    >
                      <Trash2 class="h-4 w-4" />
                    </button>
                  </td>
                </tr>
              {/each}
            </tbody>
          {:else}
            <tbody>
              <tr><td class="empty" colspan="6">No tracked domains yet.</td></tr>
            </tbody>
          {/each}
        </table>
      </div>
    </div>
  {/if}
</div>

{#if showForm}
  <Modal title="New tracked domain" onClose={closeForm}>
    <form class="space-y-4" onsubmit={submit}>
      <div>
        <label class="label" for="domain">Domain</label>
        <input id="domain" class="input" bind:value={form.domain} placeholder="acme.com" />
      </div>
      <div>
        <label class="label" for="vertical">Vertical</label>
        <select id="vertical" class="input" bind:value={form.vertical_id}>
          {#each verticalsQuery.data?.results ?? [] as v (v.id)}
            <option value={v.id}>{v.name}</option>
          {/each}
        </select>
      </div>
      <div>
        <label class="label" for="company">Company (optional)</label>
        <select id="company" class="input" bind:value={form.company_id}>
          <option value={undefined}>-- none --</option>
          {#each companiesQuery.data?.results.filter((c) => c.vertical_id === form.vertical_id) ?? [] as c (c.id)}
            <option value={c.id}>{c.name}</option>
          {/each}
        </select>
      </div>

      {#if formError}<p class="text-sm text-red-600">{formError}</p>{/if}

      <div class="flex justify-end gap-2 pt-2">
        <button type="button" class="btn-secondary" onclick={closeForm}>Cancel</button>
        <button type="submit" class="btn-primary" disabled={saveMutation.isPending}>
          {saveMutation.isPending ? 'Saving...' : 'Save'}
        </button>
      </div>
    </form>
  </Modal>
{/if}
